import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import PinDropIcon from '@material-ui/icons/PinDrop';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { ADDRESS_GRID_COLUMNS } from '../../constants/personAddressesTabConstants';
import PersonAddressLinkDialog from './PersonAddressLinkDialog';
import AddAddressDialog from './AddAddressDialog';
import { getPeopleAddresses, deletePersonAddressLink } from '../../services/userService';

const PersonAddressesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editAddressId, setEditAddressId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const canEditPerson = getFullPermissions('globals', 'People', 'any').Edit;
  const addressPerms = getFullPermissions('globals', 'Addresses', 'any');
  const canViewAddressRecord = Boolean(addressPerms.Read || addressPerms.Edit);

  const loadAddresses = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleAddresses(personId),
      onSuccess: (data) => setAddresses(data),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const handleAdd = useCallback(() => {
    setEditingLink(null);
    setLinkFormOpen(true);
  }, []);

  const handleManagePrimary = useCallback((row) => {
    setEditingLink(row);
    setLinkFormOpen(true);
  }, []);

  const handleOpenAddressRecord = useCallback((row) => setEditAddressId(row.ptsAddressID), []);

  const handleLinkFormClose = useCallback(() => {
    setLinkFormOpen(false);
    setEditingLink(null);
  }, []);

  const handleLinkSaved = useCallback(() => {
    handleLinkFormClose();
    loadAddresses();
  }, [handleLinkFormClose, loadAddresses]);

  const handleAddressRecordSaved = useCallback(() => {
    setEditAddressId(null);
    loadAddresses();
  }, [loadAddresses]);

  const handleAddressRecordClose = useCallback(() => setEditAddressId(null), []);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.FullAddressText || 'address'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () =>
              deletePersonAddressLink({ ptsPersonID: personId, ptsAddressID: row.ptsAddressID }),
            onSuccess: () => {
              dispatch(notify('Address deleted successfully', 'success'));
              loadAddresses();
            },
          });
        },
        'Delete Address'
      );
    },
    [requestConfirm, dispatch, personId, loadAddresses]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Address"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={addresses}
        columns={ADDRESS_GRID_COLUMNS}
        getRowId={(row) => row.ptsAddressID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Person Addresses',
        }}
        actions={{
          custom: [
            {
              icon: <EditIcon />,
              tooltip: 'Manage Primary',
              onClick: handleManagePrimary,
              disabled: !canEditPerson,
            },
            canViewAddressRecord && {
              icon: <PinDropIcon />,
              tooltip: 'Open Address Record',
              onClick: handleOpenAddressRecord,
              disabled: !canEditPerson,
            },
          ].filter(Boolean),
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {linkFormOpen && (
        <PersonAddressLinkDialog
          open={linkFormOpen}
          personId={personId}
          addressLink={editingLink}
          onClose={handleLinkFormClose}
          onSaved={handleLinkSaved}
        />
      )}

      {editAddressId && (
        <AddAddressDialog
          open={Boolean(editAddressId)}
          addressId={editAddressId}
          onClose={handleAddressRecordClose}
          onAddressCreated={handleAddressRecordSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonAddressesTab;
